import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth-context";
import {
    LayoutDashboard, Home, FileText, BookOpen, Palette, Image, Mail,
    LogOut, Menu, ExternalLink, Coffee, KeyRound,
} from "lucide-react";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/admin/home", label: "Home Content", icon: Home, testid: "nav-home-content" },
    { to: "/admin/pages", label: "Pages", icon: FileText, testid: "nav-pages" },
    { to: "/admin/blog", label: "Blog Posts", icon: BookOpen, testid: "nav-blog" },
    { to: "/admin/theme", label: "Theme & Brand", icon: Palette, testid: "nav-theme" },
    { to: "/admin/media", label: "Media Library", icon: Image, testid: "nav-media" },
    { to: "/admin/messages", label: "Messages", icon: Mail, testid: "nav-messages" },
    { to: "/admin/account", label: "Account & Security", icon: KeyRound, testid: "nav-account" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-cream-light text-coffee flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-coffee text-cream transform transition-transform duration-300 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                } flex flex-col`}
            >
                <div className="p-7 border-b border-cream/10">
                    <Link to="/admin" className="flex items-center gap-3">
                        <Coffee size={22} strokeWidth={1.2} className="text-gold" />
                        <div>
                            <div className="tracked text-[10px] text-gold">CAFE HIDEOUT</div>
                            <div className="font-editorial font-bold text-2xl text-cream -mt-0.5">CMS</div>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 p-5 space-y-1 overflow-y-auto">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-md tracked text-[11px] transition-all ${
                                    isActive
                                        ? "bg-gold/15 text-gold border-l-2 border-gold"
                                        : "text-cream/70 hover:text-cream hover:bg-cream/5"
                                }`
                            }
                        >
                            <item.icon size={16} strokeWidth={1.3} />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-5 border-t border-cream/10 space-y-3">
                    <a
                        href="/"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 tracked text-[10px] text-cream/60 hover:text-gold transition-colors"
                        data-testid="view-site-link"
                    >
                        <ExternalLink size={14} /> View Public Site
                    </a>
                    <div className="border-t border-cream/10 pt-3">
                        <div className="font-mono text-[10px] text-cream/40 truncate">{user?.email}</div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 mt-2 tracked text-[10px] text-cream/70 hover:text-gold transition-colors"
                            data-testid="logout-button"
                        >
                            <LogOut size={14} /> Sign Out
                        </button>
                    </div>
                </div>
            </aside>

            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-coffee/60 z-30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header className="lg:hidden sticky top-0 z-30 bg-cream-light border-b border-coffee/10 px-5 py-4 flex items-center justify-between">
                    <button onClick={() => setSidebarOpen(true)} aria-label="open menu" data-testid="open-sidebar">
                        <Menu size={22} />
                    </button>
                    <div className="tracked text-[11px]">CAFE HIDEOUT CMS</div>
                    <div className="w-6" />
                </header>
                <main className="p-6 md:p-10 max-w-[1280px] mx-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
